import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DataTable from './DataTable';
import FormModal from './FormModal';
import { getRestaurants, createRestaurant, updateRestaurant, deleteRestaurant, getDestinations } from '../../../services/travelInventoryService';

const RestaurantsManager = ({ searchQuery }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [restaurantsData, destinationsData] = await Promise.all([
        getRestaurants(),
        getDestinations(),
      ]);
      setRestaurants(restaurantsData || []);
      setDestinations(destinationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;
    
    try {
      await deleteRestaurant(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      alert('Failed to delete restaurant');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateRestaurant(editingItem?.id, formData);
      } else {
        await createRestaurant(formData);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving restaurant:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'cuisine_type', label: 'Cuisine', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    { key: 'price_range', label: 'Price Range', sortable: true },
    { key: 'average_rating', label: 'Rating', sortable: true },
  ];

  const formFields = [
    {
      name: 'destination_id',
      label: 'Destination',
      type: 'select',
      required: true,
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    { name: 'name', label: 'Restaurant Name', type: 'text', required: true },
    {
      name: 'cuisine_type',
      label: 'Cuisine Type',
      type: 'select',
      required: true,
      options: [
        { value: 'local', label: 'Local' },
        { value: 'international', label: 'International' },
        { value: 'asian', label: 'Asian' },
        { value: 'european', label: 'European' },
        { value: 'american', label: 'American' },
        { value: 'mediterranean', label: 'Mediterranean' },
        { value: 'fusion', label: 'Fusion' },
        { value: 'vegetarian', label: 'Vegetarian' },
        { value: 'vegan', label: 'Vegan' },
      ],
    },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'short_description', label: 'Short Description', type: 'textarea' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'city', label: 'City', type: 'text' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'latitude', label: 'Latitude', type: 'number', step: '0.0000001' },
    { name: 'longitude', label: 'Longitude', type: 'number', step: '0.0000001' },
    {
      name: 'price_range',
      label: 'Price Range',
      type: 'select',
      options: [
        { value: 'budget', label: 'Budget' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'expensive', label: 'Expensive' },
        { value: 'luxury', label: 'Luxury' },
      ],
    },
    { name: 'average_meal_price', label: 'Average Meal Price', type: 'number', step: '0.01' },
    { name: 'currency', label: 'Currency', type: 'text', defaultValue: 'PHP' },
    { name: 'image_url', label: 'Restaurant Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'opening_hours', label: 'Opening Hours', type: 'text' },
    { name: 'dress_code', label: 'Dress Code', type: 'text' },
    { name: 'reservation_required', label: 'Reservation Required', type: 'checkbox' },
    { name: 'reservation_url', label: 'Reservation URL', type: 'text' },
    {
      name: 'features',
      label: 'Features',
      type: 'checkbox-group',
      options: [
        { value: 'outdoor_seating', label: 'Outdoor Seating' },
        { value: 'wifi', label: 'Free WiFi' },
        { value: 'parking', label: 'Parking Available' },
        { value: 'delivery', label: 'Delivery Service' },
        { value: 'takeout', label: 'Takeout Available' },
        { value: 'wheelchair_accessible', label: 'Wheelchair Accessible' },
        { value: 'kids_menu', label: 'Kids Menu' },
        { value: 'bar', label: 'Bar/Lounge' },
        { value: 'live_music', label: 'Live Music' },
        { value: 'private_dining', label: 'Private Dining' },
      ],
    },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  ];

  const filteredData = restaurants?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    const matchesFilter = filterType === 'all' || item?.cuisine_type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Restaurants</h2>
          <p className="text-gray-600 mt-1">Manage dining options and restaurants</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Restaurant
        </Button>
      </div>

      <div className="mb-4">
        <Select
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: 'all', label: 'All Cuisines' },
            { value: 'local', label: 'Local' },
            { value: 'asian', label: 'Asian' },
            { value: 'european', label: 'European' },
            { value: 'fusion', label: 'Fusion' },
          ]}
          className="max-w-xs"
        />
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Restaurant' : 'Add Restaurant'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default RestaurantsManager;